import { useEffect, useMemo, useReducer, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';

// The one camera controller for every room. Four ways to move, all of them
// the user's hand — nothing here runs on the clock:
//
//   orbit   drag rotates, right-drag / two-finger pans, wheel zooms
//           (drei OrbitControls, wider limits than the fitted stage).
//   fly-to  a click or a number key names a station; the camera eases
//           there (600–900 ms) and any input cancels the ease.
//   cruise  a toggle (`C` / HUD chip): WASD / arrows move, Q / E rise and
//           sink, Shift is faster, drag looks around a near pivot.
//   scroll  the web pages: a Catmull-Rom path through the stations; the
//           page's scroll position is the path parameter.
//
// The owner holds a controller (`useRoomController`) and calls flyTo /
// home / setCruise / setMode / setProgress on it; the camera component
// binds the implementation on mount. The view persists per `storeKey`
// (position + target) like the stage orbit, and `nv-stage-reset` homes it.

const ease = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
const readView = (key) => { try { const v = JSON.parse(localStorage.getItem(key) || 'null'); return v && Array.isArray(v.position) && Array.isArray(v.target) ? v : null; } catch { return null; } };
const writeView = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode */ } };
const typing = (e) => { const t = e.target; return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable); };

export function useRoomController() {
  const [, bump] = useReducer((x) => x + 1, 0);
  return useMemo(() => {
    const c = {
      mode: 'orbit', cruise: false, flying: false, progress: 0, impl: null, queued: null,
      // A fly asked for before the camera mounts (deep links) waits for it.
      flyTo(pose, ms) { if (c.impl) c.impl.flyTo(pose, ms); else c.queued = { pose, ms }; },
      home(ms) { c.impl?.home(ms); },
      pose() { return c.impl?.pose() || null; },
      setCruise(v) { if (c.cruise === !!v) return; c.cruise = !!v; c.impl?.onCruise(c.cruise); bump(); },
      setMode(m) { if (c.mode === m) return; c.mode = m; c.impl?.onMode(m); bump(); },
      setProgress(p) { c.progress = Math.max(0, Math.min(1, p)); },
      nearest(stations) { return c.impl?.nearest(stations) || null; },
    };
    return c;
  }, []);
}

const CRUISE_KEYS = new Set(['w', 'a', 's', 'd', 'q', 'e', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'shift']);

export default function RoomCamera({
  ctl, home, stations = [], storeKey = null, active = true,
  polar = [0.12, 1.56], distance = [1.2, 70], speed = 7, onUserMove = null,
}) {
  const { camera } = useThree();
  const controls = useRef();
  const fly = useRef(null);
  const keys = useRef(new Set());
  const path = useMemo(() => {
    if (stations.length < 2) return null;
    const pos = new THREE.CatmullRomCurve3(stations.map((s) => new THREE.Vector3(...s.pose.position)), false, 'centripetal', 0.5);
    const tgt = new THREE.CatmullRomCurve3(stations.map((s) => new THREE.Vector3(...s.pose.target)), false, 'centripetal', 0.5);
    return { pos, tgt };
  }, [stations]);
  const tmp = useMemo(() => ({ a: new THREE.Vector3(), b: new THREE.Vector3(), f: new THREE.Vector3(), r: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0) }), []);

  const place = (pose) => {
    camera.position.set(...pose.position);
    controls.current?.target.set(...pose.target);
    camera.lookAt(...pose.target);
    controls.current?.update();
  };
  const startFly = (pose, ms = 800) => {
    if (!pose) return;
    fly.current = {
      from: { p: camera.position.clone(), t: controls.current ? controls.current.target.clone() : new THREE.Vector3() },
      to: { p: new THREE.Vector3(...pose.position), t: new THREE.Vector3(...pose.target) },
      t0: performance.now(), ms,
    };
    ctl.flying = true;
  };

  // Bind the controller.
  useEffect(() => {
    ctl.impl = {
      flyTo: startFly,
      home: (ms) => { if (storeKey) try { localStorage.removeItem(storeKey); } catch { /* ignore */ } startFly(home, ms); },
      pose: () => ({ position: camera.position.toArray(), target: controls.current ? controls.current.target.toArray() : [0, 0, 0] }),
      onCruise: (on) => {
        fly.current = null;
        if (on && controls.current) {
          // Look around a near pivot: drag turns the head, not the world.
          camera.getWorldDirection(tmp.f);
          controls.current.target.copy(camera.position).addScaledVector(tmp.f, 3);
          controls.current.update();
        }
      },
      onMode: () => { fly.current = null; },
      nearest: (list) => {
        let best = null; let bd = Infinity;
        for (const s of list) { const d = camera.position.distanceTo(tmp.a.set(...s.pose.position)); if (d < bd) { bd = d; best = s; } }
        return best;
      },
    };
    return () => { if (ctl.impl) ctl.impl = null; };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // First placement: the saved view, else home. Scroll mode starts on the path.
  useEffect(() => {
    if (ctl.mode === 'scroll' && path) { place({ position: path.pos.getPoint(ctl.progress).toArray(), target: path.tgt.getPoint(ctl.progress).toArray() }); return; }
    place((storeKey && readView(storeKey)) || home);
    if (ctl.queued) { const q = ctl.queued; ctl.queued = null; startFly(q.pose, q.ms); }
  }, [home, storeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onReset = (e) => { if (!storeKey || !e.detail || e.detail === storeKey) ctl.home(); };
    const down = (e) => {
      if (typing(e)) return;
      const k = e.key.toLowerCase();
      if (CRUISE_KEYS.has(k)) { keys.current.add(k); if (ctl.cruise && k !== 'shift') { fly.current = null; e.preventDefault(); } }
    };
    const up = (e) => keys.current.delete(e.key.toLowerCase());
    const blur = () => keys.current.clear();
    window.addEventListener('nv-stage-reset', onReset);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('nv-stage-reset', onReset);
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [ctl, storeKey]);

  const save = () => { if (storeKey && ctl.mode === 'orbit') writeView(storeKey, ctl.impl?.pose()); onUserMove?.(); };

  useFrame((_, dt) => {
    const c = controls.current;
    const step = Math.min(0.05, dt || 0.016);
    if (ctl.mode === 'scroll' && path) {
      // The page's scroll is the camera: ease toward the path point.
      const k = 1 - Math.exp(-step * 7);
      path.pos.getPoint(ctl.progress, tmp.a);
      path.tgt.getPoint(ctl.progress, tmp.b);
      camera.position.lerp(tmp.a, k);
      if (c) { c.target.lerp(tmp.b, k); c.update(); } else camera.lookAt(tmp.b);
      return;
    }
    if (fly.current) {
      const f = fly.current;
      const k = ease(Math.min(1, (performance.now() - f.t0) / f.ms));
      camera.position.lerpVectors(f.from.p, f.to.p, k);
      if (c) c.target.lerpVectors(f.from.t, f.to.t, k);
      if (k >= 1) { fly.current = null; ctl.flying = false; }
      c?.update();
      return;
    }
    if (ctl.cruise && keys.current.size) {
      const ks = keys.current;
      const v = speed * (ks.has('shift') ? 3 : 1) * step;
      camera.getWorldDirection(tmp.f);
      tmp.r.crossVectors(tmp.f, tmp.up).normalize();
      tmp.a.set(0, 0, 0);
      if (ks.has('w') || ks.has('arrowup')) tmp.a.addScaledVector(tmp.f, v);
      if (ks.has('s') || ks.has('arrowdown')) tmp.a.addScaledVector(tmp.f, -v);
      if (ks.has('d') || ks.has('arrowright')) tmp.a.addScaledVector(tmp.r, v);
      if (ks.has('a') || ks.has('arrowleft')) tmp.a.addScaledVector(tmp.r, -v);
      if (ks.has('e')) tmp.a.y += v;
      if (ks.has('q')) tmp.a.y -= v;
      if (tmp.a.lengthSq() > 0) {
        camera.position.add(tmp.a);
        if (c) { c.target.add(tmp.a); c.update(); }
      }
    }
  });

  return (
    <OrbitControls
      ref={controls}
      makeDefault
      enabled={active && ctl.mode !== 'scroll'}
      enablePan={!ctl.cruise}
      enableDamping
      dampingFactor={0.08}
      rotateSpeed={0.5}
      panSpeed={0.8}
      zoomSpeed={0.7}
      screenSpacePanning={false}
      minDistance={distance[0]}
      maxDistance={distance[1]}
      minPolarAngle={polar[0]}
      maxPolarAngle={polar[1]}
      onStart={() => { fly.current = null; ctl.flying = false; }}
      onEnd={save}
    />
  );
}
